import {
  RemoveBlockedContactPayload,
  RemoveBlockedContactResult,
  UpsertBlockedContactPayload,
  UpsertBlockedContactResult,
} from "@/core";
import { repositoryHost } from "@/repositories";
import { serviceHost } from "@/services";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const databaseService = serviceHost.getDatabaseService();
const functionsService = serviceHost.getFunctionsService();

const blockedContactRepository =
  repositoryHost.getBlockedContactRepository(databaseService);

export const useBlockedContacts = () => {
  return useQuery({
    queryKey: ["blocked-contacts", "get"],
    queryFn: async () =>
      await blockedContactRepository.getAll({
        orderBy: {
          field: "email",
          direction: "asc",
        },
      }),
  });
};

// Writes go through callables rather than straight to Firestore: the document
// ID has to be the normalized blocklist key the submission guard looks up, and
// that normalization lives in the backend. Normalizing here as well would drift
// and produce blocks that silently never match.
export const useUpsertBlockedContact = () => {
  const queryClient = useQueryClient();

  return useMutation<
    UpsertBlockedContactResult,
    Error,
    UpsertBlockedContactPayload
  >({
    mutationKey: ["blocked-contacts", "upsert"],
    mutationFn: (payload) => functionsService.upsertBlockedContact(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["blocked-contacts", "get"] });
    },
  });
};

export const useRemoveBlockedContact = () => {
  const queryClient = useQueryClient();

  return useMutation<
    RemoveBlockedContactResult,
    Error,
    RemoveBlockedContactPayload
  >({
    mutationKey: ["blocked-contacts", "remove"],
    mutationFn: (payload) => functionsService.removeBlockedContact(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["blocked-contacts", "get"] });
    },
  });
};
